import React from "react";
import { useForm } from "react-hook-form";
import BackDrop from "../../../components/ui/BackDrop";
import { cn } from "../../../utils/twMerge";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomDateInput2 from "../../../components/ui/custom-date-input/CustomDateInput2";

import { datePickerIcon, closeIcon } from "../../../assetData/icons";
import useSelectedFinYear from "../../../utils/selectedFinyear";
import MirageLoader from "../../../components/ui/pageLoader/MirageLoader";
import CustomDateInput from "../../../components/ui/custom-date-input/CustomDateInput";

const MasterScriptEdit = ({
  buttonConfig,
  selectedItemId,
  isButtonClicked,
}) => {
  const getOneHandler = buttonConfig.getOneHandler;
  const editHandler = buttonConfig.editHandler;
  const closeHandler = buttonConfig.closeEditModal;
  const { currentDateStr } = useSelectedFinYear();

  const schema = z.object({
    file_id: z.string().nonempty(),
    serial_record_number: z.string().nonempty(),
    script_name: z.string().nonempty(),
    isin: z.string().nonempty(),
    nse_symbol: z.string().nullable(),
    nse_series: z.string().nullable(),
    nse_lot: z.string().nullable(),
    nse_face_value: z.string().nullable(),
    bse_code: z.string().nullable(),
    bse_lot: z.string().nullable(),
    bse_face_value: z.string().nullable(),
    bse_group: z.string(),
    script_type_name: z.string().nonempty(),
    asset_class_name: z.string().nonempty(),
    vidal_sector_code: z.string().nonempty(),
    ipo_allotment_price: z.string().nullable(),
    ipo_allotment_date: z.string().nullable(),
  });

  const form = useForm({
    defaultValues: async () => {
      const data = await getOneHandler(selectedItemId);

      return {
        id: data.id,
        status: data.status,
        file_id: data.file_id,
        serial_record_number: data.serial_record_number,
        script_name: data.script_name,
        isin: data.isin,
        nse_symbol: data.nse_symbol,
        nse_series: data.nse_series,
        nse_lot: String(data.nse_lot),
        nse_face_value: data.nse_face_value,
        bse_code: String(data.bse_code),
        bse_lot: String(data.bse_lot),
        bse_face_value: data.bse_face_value,
        bse_group: data.bse_group,
        script_type_name: data.script_type_name,
        asset_class_name: data.asset_class_name,
        vidal_sector_code: String(data.vidal_sector_code),
        ipo_allotment_price: data.ipo_allotment_price
          ? String(data.ipo_allotment_price)
          : "",
        ipo_allotment_date: data.ipo_allotment_date
          ? data.ipo_allotment_date
          : "",

        status: data.active_status,
        created_on: data.created_on,
        updated_on: data.updated_on,
        updated_by: data.updated_by,
      };
    },
    resolver: zodResolver(schema),
  });

  const errors = form.formState.errors;

  const onSubmit = async (data) => {
    const statusValue = form.getValues("status") === "true";
    const updatedData = {
      ...data,
      status: statusValue,
      ipo_allotment_date:
        data.ipo_allotment_date !== "" ? data.ipo_allotment_date : null,
      ipo_allotment_price:
        data.ipo_allotment_price !== "" ? data.ipo_allotment_price : null,
    };
    const res = await editHandler(selectedItemId, updatedData);
    if (res) closeHandler();
  };

  const onClose = () => {
    closeHandler();
  };

  return (
    <>
      <div className="fixed left-1/2 z-50 mx-auto min-h-[300px] w-[98%] max-w-[1200px] -translate-x-1/2 rounded bg-white p-8 max-md:top-[10%] md:top-1/2 md:-translate-y-1/2">
        <h2 className="pb-4 text-2xl font-bold text-neutral-800">Edit Scrip</h2>

        <button
          type="button"
          className="absolute right-8 top-8"
          onClick={onClose}
        >
          <img src={closeIcon} />
        </button>

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="max-md:scrollbar-custom max-md:h-[560px] max-md:overflow-y-scroll [&>div>div>div>input]:w-full ">
            {/* row 1 */}
            <div className="gap-2 md:flex ">
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">File ID *</label>
                  <input
                    placeholder="Type here"
                    {...form.register("file_id")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.file_id && "border-[1px] border-red-300",
                    )}
                  />
                </div>

                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    Serial Record Number *
                  </label>
                  <input
                    placeholder="Type here"
                    {...form.register("serial_record_number")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.serial_record_number &&
                        "border-[1px] border-red-300",
                    )}
                  />
                </div>
              </div>
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    Scrip Name *
                  </label>
                  <input
                    placeholder="Type here"
                    {...form.register("script_name")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.script_name && "border-[1px] border-red-300",
                    )}
                  />
                </div>

                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">ISIN *</label>
                  <input
                    placeholder="Type here"
                    {...form.register("isin")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.isin && "border-[1px] border-red-300",
                    )}
                  />
                </div>
              </div>
            </div>

            {/* row 2 */}
            <div className="gap-2 md:mt-8 md:flex">
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    NSE Symbol *
                  </label>
                  <input
                    placeholder="Type here"
                    {...form.register("nse_symbol")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.nse_symbol && "border-[1px] border-red-300",
                    )}
                  />
                </div>
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    NSE Series{" "}
                  </label>
                  <input
                    placeholder="Type here"
                    {...form.register("nse_series")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.nse_series && "border-[1px] border-red-300",
                    )}
                  />
                </div>
              </div>
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">NSE Lot</label>
                  <input
                    placeholder="Type here"
                    type="number"
                    {...form.register("nse_lot")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.nse_lot && "border-[1px] border-red-300",
                    )}
                  />
                </div>

                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    NSE Face Value
                  </label>
                  <input
                    placeholder="Type here"
                    type="number"
                    {...form.register("nse_face_value")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.nse_face_value && "border-[1px] border-red-300",
                    )}
                  />
                </div>
              </div>
            </div>

            {/* row 3 */}
            <div className="gap-2 md:mt-8 md:flex">
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">BSE Code</label>
                  <input
                    placeholder="Type here"
                    type="number"
                    {...form.register("bse_code")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.bse_code && "border-[1px] border-red-300",
                    )}
                  />
                </div>

                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">BSE Lot</label>
                  <input
                    placeholder="Type here"
                    type="number"
                    {...form.register("bse_lot")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.bse_lot && "border-[1px] border-red-300",
                    )}
                  />
                </div>
              </div>

              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    BSE Face Value
                  </label>
                  <input
                    placeholder="Type here"
                    type="number"
                    {...form.register("bse_face_value")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.bse_face_value && "border-[1px] border-red-300",
                    )}
                  />
                </div>

                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">BSE Group</label>
                  <input
                    placeholder="Type here"
                    {...form.register("bse_group")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.bse_group && "border-[1px] border-red-300",
                    )}
                  />
                </div>
              </div>
            </div>

            {/* row 4 */}
            <div className="gap-2 md:mt-8 md:flex">
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    Security Type *
                  </label>
                  <input
                    placeholder="Type here"
                    {...form.register("script_type_name")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.script_type_name && "border-[1px] border-red-300",
                    )}
                  />
                </div>

                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    Asset Class *
                  </label>
                  <input
                    placeholder="Type here"
                    {...form.register("asset_class_name")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.asset_class_name && "border-[1px] border-red-300",
                    )}
                  />
                </div>
              </div>
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    Vidal Code *
                  </label>
                  <input
                    placeholder="Type here"
                    type="number"
                    {...form.register("vidal_sector_code")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.vidal_sector_code && "border-[1px] border-red-300",
                    )}
                  />
                </div>
                <div className="flex h-full  w-full flex-col gap-2">
                  <label className="text-[#636363]">Status</label>
                  <div
                    className={cn(
                      " h-full rounded bg-neutral-100 px-2 py-[8px]",
                      errors.status && "border-[1px] border-red-300",
                    )}
                  >
                    <select
                      {...form.register("status")}
                      className=" w-full  bg-transparent px-2 text-[#636363] focus:outline-none"
                      defaultValue={true}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            {/* row 5 */}
            <div className="gap-2 md:mt-8 md:flex">
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex">
                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    IPO Allotment Price
                  </label>
                  <input
                    placeholder="Type here"
                    step={"any"}
                    type="number"
                    {...form.register("ipo_allotment_price")}
                    className={cn(
                      "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
                      errors.ipo_allotment_price &&
                        "border-[1px] border-red-300",
                    )}
                  />
                </div>

                <div className={cn("flex w-full flex-col  gap-2")}>
                  <label className="text-base text-[#636363]">
                    IPO Allotment Date
                  </label>
                  <CustomDateInput
                    initialValue={form.getValues("ipo_allotment_date")}
                    form1={form.register("ipo_allotment_date")}
                    className="w-full"
                    max={currentDateStr}
                  />
                </div>
              </div>
              <div className="w-full gap-2 max-md:mt-3 max-sm:space-y-3 sm:flex"></div>
            </div>
          </div>

          {/* action */}
          <div className="flex items-end justify-between gap-2 max-md:[&>button]:w-1/2 max-sm:[&>button]:w-full md:[&>button]:w-1/4">
            <button
              type="submit"
              disabled={isButtonClicked}
              className={` ${
                isButtonClicked ? "opacity-[70%]" : ""
              } mt-8 w-full rounded border-2 border-pri-clr-orange bg-pri-clr-orange px-2 py-2 font-semibold text-white `}
            >
              {isButtonClicked ? <MirageLoader /> : "SAVE CHANGES"}
            </button>
          </div>
        </form>
      </div>
      <BackDrop />
    </>
  );
};

export default MasterScriptEdit;
